import type { Session } from "@supabase/supabase-js";
import type { PayableStatus } from "@tinkr/shared/payments";

import { idempotencyKey, TinkrApiError, tinkrApiFetch } from "./client";
import { evidencePhotoContentType } from "./provider-evidence-photo";

export type ProviderOfferStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "expired"
  | "reclaimed";
// The /api provider payables status domain IS the shared PAYABLE_STATUSES
// constant (@tinkr/shared/payments): the ADR-P11 payout lifecycle plus the
// still-live legacy settlement states, pinned to the M5 payables_status_check
// by the /api migration-shape test. Aliased (not re-mirrored) so this type can
// never drift behind the DB again.
export type ProviderPayableStatus = PayableStatus;

export type QualityIncidentStatus =
  | "open"
  | "acknowledged"
  | "provider_responded"
  | "resolved"
  | "voided";
export type QualityIncidentSeverity = "minor" | "major" | "critical";
export type QualityIncidentReasonCode =
  | "dimensional_accuracy"
  | "surface_quality"
  | "wrong_material"
  | "wrong_color"
  | "missing_part"
  | "late_dispatch"
  | "damaged_in_transit"
  | "missing_evidence"
  | "spec_mismatch"
  | "customer_reported"
  | "other"
  // Provider-report reason codes (a provider opening a problem on their own job).
  | "print_failure"
  | "material_unavailable"
  | "spec_impossible"
  | "customer_unreachable";

/** Reason codes a PROVIDER may select when reporting a problem on their job.
 * `other` is the shared catch-all; the rest are provider-worded above. */
export const providerReportReasonCodes = [
  "print_failure",
  "material_unavailable",
  "spec_impossible",
  "customer_unreachable",
  "other"
] as const satisfies readonly QualityIncidentReasonCode[];

export type ProviderReportReasonCode = (typeof providerReportReasonCodes)[number];

export type ProviderOfferOrderRequirements = {
  priorityPreset: "quality" | "balanced" | "speed" | "price";
  allowSplit: boolean;
  sameProviderRequired: boolean;
  destinationCountryCode: string | null;
  shippingServiceLevel: "standard" | "tracked" | "express";
  qualityLevel: "standard" | "documented" | "certified_provider" | "regulated_manual_review";
  qualityEvidenceRequirements: string[];
  certificationNotes: string | null;
  deadlineAt: string | null;
  additionalNotes: string | null;
  reorderSourceJobId: string | null;
};

export type ProviderOfferPartRequirements = {
  layerHeightMm: number | null;
  infillPct: number | null;
  supportsPreference: "provider_decides" | "required" | "avoid_if_possible" | null;
  toleranceNotes: string | null;
  surfaceFinish: string | null;
  postProcessing: string[];
  additionalNotes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProviderOfferPart = {
  id: string;
  inputArtifactId: string;
  /** Measured printable envelope. Absent/null only for legacy artifacts. */
  dimensionsMm?: { x: number; y: number; z: number } | null;
  displayName: string | null;
  quantity: number;
  materialId: string | null;
  colorRef: string | null;
  qualityPreset: "quality" | "balanced" | "speed" | "price" | null;
  notes: string | null;
  requirements: ProviderOfferPartRequirements | null;
  /** ADR-M3 §2: latest slice estimate for this part's input artifact (null until
   * the slice has produced an estimate). */
  estimatedFilamentG: number | null;
  estimatedPrintSeconds: number | null;
  createdAt: string;
  updatedAt: string;
};

export type ProviderOffer = {
  id: string;
  jobId: string;
  fulfillmentGroupId: string;
  providerId: string;
  offeredByActorType: "admin" | "system";
  offeredByActorId: string;
  status: ProviderOfferStatus;
  /** Pre-accept offer TTL. Once `status` is `accepted`, this timestamp is dead
   * (frozen at whatever it was pre-accept) and must not be surfaced as a live
   * deadline — use `productionDueAt` / `shipDueAt` instead. */
  expiresAt: string;
  /** Deadline to move the job to `printing` (Start Printing), stamped when the
   * offer is accepted. Null while pending, and null on offers accepted before
   * the fulfillment-deadline rollout — tolerate absence, do not default to a
   * fabricated date. */
  productionDueAt?: string | null;
  /** Deadline to move the job to `delivered` (Mark Delivered), stamped when the
   * offer is accepted. Same absence rules as `productionDueAt`. */
  shipDueAt?: string | null;
  /** When the provider marked the order shipped (recorded the shipment). The
   * hand-off happened, so the ship deadline no longer applies. Older APIs omit
   * it. */
  shippedAt?: string | null;
  /** When a shipped order becomes delivered automatically unless the customer
   * confirms sooner (founder decision 2026-10-27). Present only while the order
   * is still shipped; older APIs omit it. */
  autoDeliverAt?: string | null;
  /** When the job completed (the customer confirmed receipt, or it completed
   * automatically after delivery). Null unless completed; older APIs omit it. */
  completedAt?: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  /** Server-derived current lifecycle state of the underlying job (e.g.
   * `quote_locked` | `delivered` | `completed`). Drives the fulfillment action
   * off real state rather than local memory, so it survives reload. */
  jobState: string | null;
  /** First-party/source category used to distinguish Doodles from other work. */
  originChannel?: string | null;
  /** True when the job must be fulfilled by a premium print provider
   * (`jobs.premium_required`). Older APIs omit it; treat absence as false.
   * Providers never see the premium uplift rate or any other platform
   * economics; the uplift is already inside the payout (ADR premium §9.2). */
  premium?: boolean;
  /** Why a cancelled job ended when the server can tell. `refunded` is
   * confirmed; `refund_pending` means processing/review is not yet complete.
   * Open string — newer server values must degrade readably, never crash. */
  cancellationReason?: string | null;
  colorMode?: "solid" | "multicolor";
  requiredColors?: Array<{
    slotIndex: number;
    filamentColorId: string;
    hex: string;
    idealHex?: string;
    name: string;
    coveragePct: number;
  }>;
  /** ADR-M3 §2: offer-time provider payout snapshot (payout-neutral naming), or
   * null when no snapshot was captured. */
  payoutMinor?: number | null;
  payoutCurrency?: string | null;
  /** Immutable offer-time payout components. Manufacturing already includes any minimum-order top-up. */
  payoutBreakdown?: {
    manufacturingMinor: number;
    shippingMinor: number;
    totalMinor: number;
    minimumOrderAdjustmentMinor: number;
    currency: string;
  };
  /** Non-PII Doodles manufacturing dimensions selected by the buyer. */
  doodles?: {
    sizeSku: "S" | "M";
    targetHeightMm: number | null;
  };
  order?: {
    requirements: ProviderOfferOrderRequirements | null;
    parts: ProviderOfferPart[];
  };
  createdAt: string;
  updatedAt: string;
};

/** ADR-M3 §1: preview while the offer is pending, download once accepted. */
export type ModelAccessKind = "preview" | "download";

export type ProviderOfferModelAccessResponse = {
  ok: true;
  url: string;
  expiresAt: string;
  accessKind: ModelAccessKind;
  fileName: string;
  byteSize: number | null;
  /** STL geometry or final multicolor 3MF print plan. */
  artifactType?: "stl" | "3mf";
  /** Selected solid swatch for the authorized artifact; absent for older APIs. */
  colorHex?: string | null;
  /** @deprecated Original generation preview; render the final 3MF instead. */
  previewGlbUrl?: string | null;
};

export type ProviderPrintablePartsAttempt = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  creditsConsumed: number | null;
  byteSize: number | null;
  errorCode: string | null;
  requestedAt: string;
  completedAt: string | null;
};

export type ProviderPrintablePartsStatusResponse = {
  ok: true;
  originalRetained: true;
  attempt: ProviderPrintablePartsAttempt | null;
};

export type ProviderPrintablePartsDownloadResponse = {
  ok: true;
  url: string;
  expiresAt: string;
  fileName: "model-parts.stl";
  byteSize: number;
};

/** JP shipping shape (Japan Post ordering + kana + honorific-free name). */
export type JpShippingAddress = {
  countryCode: "JP";
  postalCode: string;
  prefecture: string;
  city: string;
  town: string;
  building: string;
  recipientName: string;
  recipientNameKana: string;
  phone: string;
};

/** Western structured shape (non-JP enabled markets, e.g. MY). */
export type WesternShippingAddress = {
  countryCode: string;
  recipientName: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode: string;
  phone?: string;
};

export type StructuredShippingAddress = JpShippingAddress | WesternShippingAddress;

export type ProviderJobShippingAddressResponse = {
  ok: true;
  /** The provider-safe structured address, or null when the job has no street
   * address on file (first-party guest orders store one). */
  address: StructuredShippingAddress | null;
  destinationCountryCode: string | null;
};

export type ProviderOfferResponse = {
  ok: true;
  offer: ProviderOffer;
  state?: string | null;
  eventId?: string | null;
};

/** Current platform fulfillment-deadline policy (hours from acceptance). */
export type FulfillmentPolicy = {
  productionDeadlineHours: number;
  shipDeadlineHours: number;
  /** Extra hand-off hours added on top of `shipDeadlineHours` when the accepted
   * job is multicolor (multi-filament changes take longer to finish and pack).
   * Optional: older API deploys omit it, and absence means no extension (0) —
   * never substitute a fabricated extension. */
  multicolorShipDeadlineExtraHours?: number;
  /** Days after shipping before an order counts as delivered automatically
   * unless the customer confirms sooner. Optional: older API deploys omit it. */
  autoDeliverAfterDays?: number;
};

/** Fallback used whenever the server omits `fulfillmentPolicy` on the offers
 * list response (older API deploys, or a proxy that strips unknown fields).
 * Consumers apply this default rather than the type carrying it implicitly,
 * so a real server value always wins when present. */
export const DEFAULT_FULFILLMENT_POLICY: FulfillmentPolicy = {
  productionDeadlineHours: 24,
  shipDeadlineHours: 72
};

/** Hand-off hours the provider commits to for THIS offer: the flat window plus the
 * multicolor extension when the job is multicolor. Absent extension = 0. */
export function effectiveShipDeadlineHours(
  policy: FulfillmentPolicy,
  offer: Pick<ProviderOffer, "colorMode">
): number {
  if (offer.colorMode !== "multicolor") {
    return policy.shipDeadlineHours;
  }
  return policy.shipDeadlineHours + (policy.multicolorShipDeadlineExtraHours ?? 0);
}

export type ProviderOffersResponse = {
  ok: true;
  offers: ProviderOffer[];
  /** Opaque keyset cursor for the next page; absent on older API deploys. */
  nextCursor?: string | null;

  /** Current policy the accept-confirmation and deadline copy is based on.
   * Optional: older API deploys omit it, so consumers fall back to
   * `DEFAULT_FULFILLMENT_POLICY`. */
  fulfillmentPolicy?: FulfillmentPolicy;
};

export type ProviderPayable = {
  id: string;
  jobId: string;
  fulfillmentGroupId: string;
  quoteId: string;
  providerPayout: {
    amountMinor: number;
    currency: string;
  };
  status: ProviderPayableStatus;
  expiresAt: string;
  settledAt: string | null;
  paidAt: string | null;
  createdAt: string;
  completionHoldActive: boolean;
};

export type ProviderPayablesResponse = {
  ok: true;
  providerId: string;
  payables: ProviderPayable[];
};

export type ProviderFulfillmentStrikeKind =
  | "production_overdue_reclaimed"
  | "delivery_overdue_reclaimed"
  | "provider_withdrew";

export type ProviderFulfillmentStrike = {
  // Known kinds get a translated label; the API deliberately passes through
  // kinds a future migration adds, so keep this open to plain strings and let
  // the label map fall back rather than crash.
  kind: ProviderFulfillmentStrikeKind | (string & {});
  reason: string;
  jobId: string;
  createdAt: string;
};

export type ProviderFulfillmentStanding = {
  strikeCount: number;
  strikeWindowDays: number;
  strikePauseThreshold: number;
  fulfillmentPausedAt: string | null;
  fulfillmentPauseReason: string | null;
  strikes: ProviderFulfillmentStrike[];
};

export type ProviderFulfillmentStandingResponse = {
  ok: true;
  standing: ProviderFulfillmentStanding;
};

export type QualityIncident = {
  id: string;
  jobId: string;
  fulfillmentGroupId: string | null;
  providerId: string;
  payableId: string | null;
  status: QualityIncidentStatus;
  severity: QualityIncidentSeverity;
  reasonCode: QualityIncidentReasonCode;
  summary: string;
  providerResponse: string | null;
  resolutionNote: string | null;
  openedByActorType: "admin" | "system" | "provider";
  openedByActorId: string;
  openedAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProviderQualityIncidentsResponse = {
  ok: true;
  providerId: string;
  qualityIncidents: QualityIncident[];
  nextCursor?: string | null;
};

export type QualityIncidentCommandResponse = {
  ok: true;
  command: {
    accepted: boolean;
    incidentId?: string;
    jobId?: string;
    providerId?: string;
    fulfillmentGroupId?: string | null;
    payableId?: string | null;
    status?: QualityIncidentStatus;
    eventId?: string;
    idempotentReplay?: boolean;
  };
};

export type ProviderPayablesQuery = {
  status?: ProviderPayableStatus;
  limit?: number;
};

export type ProviderQualityIncidentsQuery = {
  status?: QualityIncidentStatus;
  jobId?: string;
  limit?: number;
  cursor?: string;
};
export type ProviderOffersQuery = {
  limit?: number;
  cursor?: string;
  /** Accepted offers whose job is still in progress only (not delivered,
   * completed or cancelled). Optional, additive filter: an API deploy that
   * predates it ignores the parameter and returns the unfiltered list, so
   * callers keep their own client-side filter (see provider-work-resource.ts). */
  jobActive?: boolean;
  /** Completed jobs only (the work queue's Completed list). Optional, additive
   * filter: an API deploy that predates it returns the unfiltered list, so the
   * caller keeps its own client-side filter. Never combined with jobActive. */
  jobState?: "completed";
};

/** Query parameter name of the in-progress filter (WQ-2). */
export const PROVIDER_OFFERS_JOB_ACTIVE_PARAM = "jobActive";

export function listProviderOffers(
  session: Session,
  status?: ProviderOfferStatus,
  options: ProviderOffersQuery = {},
  signal?: AbortSignal
): Promise<ProviderOffersResponse> {
  const query = new URLSearchParams();
  if (status) query.set("status", status);
  query.set("limit", String(options.limit ?? 50));
  if (options.cursor) query.set("cursor", options.cursor);
  if (options.jobActive) query.set(PROVIDER_OFFERS_JOB_ACTIVE_PARAM, "true");
  if (options.jobState) query.set("jobState", options.jobState);
  return tinkrApiFetch<ProviderOffersResponse>(
    session,
    `/v1/provider/offers?${query.toString()}`,
    { signal }
  );
}

/**
 * Why a missed offer closed, derived server-side: the respond-by time passed,
 * another provider accepted first, the order was cancelled, or Tinkr withdrew
 * it (reassignment or an order change).
 */
export type ProviderMissedOfferOutcome =
  | "timed_out"
  | "taken_by_another_provider"
  | "order_cancelled"
  | "withdrawn";

/** Trimmed view of an offer that closed before this provider accepted. */
export type ProviderMissedOffer = {
  id: string;
  outcome: ProviderMissedOfferOutcome;
  offeredAt: string;
  respondBy: string;
  closedAt: string;
  originChannel: string | null;
  colorMode: "solid" | "multicolor";
  premium: boolean;
  payoutMinor: number | null;
  payoutCurrency: string | null;
  destinationCountryCode: string | null;
  qualityLevel: string | null;
  doodles?: { sizeSku: "S" | "M"; targetHeightMm: number | null };
  parts: Array<{
    displayName: string | null;
    quantity: number;
    materialId: string | null;
    colorRef: string | null;
    dimensionsMm?: { x: number; y: number; z: number } | null;
    estimatedFilamentG: number | null;
    estimatedPrintSeconds: number | null;
  }>;
};

export type ProviderMissedOffersResponse = {
  ok: true;
  windowDays: number;
  missedOffers: ProviderMissedOffer[];
};

export function listProviderMissedOffers(
  session: Session,
  options: { limit?: number } = {},
  signal?: AbortSignal
): Promise<ProviderMissedOffersResponse> {
  const query = new URLSearchParams({ limit: String(options.limit ?? 20) });
  return tinkrApiFetch<ProviderMissedOffersResponse>(
    session,
    `/v1/provider/missed-offers?${query.toString()}`,
    { signal }
  );
}

export function getProviderOffer(
  session: Session,
  offerId: string
): Promise<ProviderOfferResponse> {
  return tinkrApiFetch<ProviderOfferResponse>(
    session,
    `/v1/provider/offers/${encodeURIComponent(offerId)}`
  );
}

/**
 * ADR-M3 §1: mint a short-lived (15-minute) signed link to the job's verified
 * STL. `accessKind` is `preview` while the offer is pending and `download` once
 * accepted. Every call is logged server-side.
 */
export function fetchProviderOfferModelAccess(
  session: Session,
  offerId: string
): Promise<ProviderOfferModelAccessResponse> {
  return tinkrApiFetch<ProviderOfferModelAccessResponse>(
    session,
    `/v1/provider/offers/${encodeURIComponent(offerId)}/model-access`
  );
}

export function getProviderPrintablePartsStatus(
  session: Session,
  jobId: string
): Promise<ProviderPrintablePartsStatusResponse> {
  return tinkrApiFetch(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/printable-parts`
  );
}

export function requestProviderPrintableParts(
  session: Session,
  jobId: string
): Promise<ProviderPrintablePartsStatusResponse> {
  return tinkrApiFetch(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/printable-parts`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("provider-printable-parts") },
      body: JSON.stringify({})
    }
  );
}

export function fetchProviderPrintablePartsDownload(
  session: Session,
  jobId: string
): Promise<ProviderPrintablePartsDownloadResponse> {
  return tinkrApiFetch(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/printable-parts/download`
  );
}

/**
 * ADR-M3 §1: the assigned provider reads the buyer shipping address for a job
 * they are fulfilling (post-acceptance only). Returns the stored structured
 * address, or country-only when no street address is on file.
 */
export function fetchProviderJobShippingAddress(
  session: Session,
  jobId: string
): Promise<ProviderJobShippingAddressResponse> {
  return tinkrApiFetch<ProviderJobShippingAddressResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/shipping-address`
  );
}

export function acceptProviderOffer(
  session: Session,
  offerId: string
): Promise<ProviderOfferResponse> {
  return tinkrApiFetch<ProviderOfferResponse>(
    session,
    `/v1/provider/offers/${encodeURIComponent(offerId)}/accept`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-offer-accept")
      }
    }
  );
}

/**
 * Language-neutral decline reasons (CORE-10, founder-approved additive API
 * change). Sent as `declineReasonCode` with the free-text note in the existing
 * `declineReason` field, so reasons can be aggregated instead of being stored
 * as one of 11 translated labels. Must equal the API's closed set
 * (`PROVIDER_OFFER_DECLINE_REASON_CODES`, which the API branch adds to
 * `@tinkr/shared/provider-work`); once that module is on the target branch,
 * import it here instead of repeating the list. ADR-PWQ1 records the contract
 * and the deploy order.
 */
export const providerDeclineReasonCodes = [
  "capacity_full",
  "material_unavailable",
  "cannot_meet_deadline",
  "quality_requirements",
  "other"
] as const;

export type ProviderDeclineReasonCode = (typeof providerDeclineReasonCodes)[number];

/** The /api `declineReason` bound (trimmed string, 1..500). */
export const PROVIDER_DECLINE_REASON_MAX = 500;

export type ProviderDeclineRequest = {
  reasonCode: ProviderDeclineReasonCode;
  /** Optional free text, at most PROVIDER_DECLINE_REASON_MAX characters. */
  note?: string;
  /**
   * The pre-reason-code wire form ("<label>: <note>", already within the
   * 500-character bound). Sent only when the API rejects `declineReasonCode`
   * because it predates reason codes, so the client works in either deploy
   * order.
   */
  legacyReason: string;
};

function postDecline(session: Session, offerId: string, body: Record<string, string>) {
  return tinkrApiFetch<ProviderOfferResponse>(
    session,
    `/v1/provider/offers/${encodeURIComponent(offerId)}/decline`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-offer-decline")
      },
      body: JSON.stringify(body)
    }
  );
}

/** True when the API answered a coded decline with a validation error about
 * the reason-code field: an API from before reason codes (strict schema,
 * unknown key) or with a different code list. Nothing was committed. */
export function isDeclineReasonCodeUnsupported(error: unknown): boolean {
  if (!(error instanceof TinkrApiError)) return false;
  if (error.status !== 400 || error.code !== "validation_failed") return false;
  try {
    return JSON.stringify(error.details ?? null).includes("declineReasonCode");
  } catch {
    return false;
  }
}

export async function declineProviderOffer(
  session: Session,
  offerId: string,
  reason?: string | ProviderDeclineRequest
): Promise<ProviderOfferResponse> {
  if (reason === undefined || typeof reason === "string") {
    const legacy = reason?.trim();
    return postDecline(session, offerId, legacy ? { declineReason: legacy } : {});
  }
  const note = reason.note?.trim().slice(0, PROVIDER_DECLINE_REASON_MAX);
  try {
    return await postDecline(session, offerId, {
      declineReasonCode: reason.reasonCode,
      ...(note ? { declineReason: note } : {})
    });
  } catch (error) {
    if (!isDeclineReasonCodeUnsupported(error)) throw error;
    // The rejected request failed validation before any transition ran, so a
    // second request (fresh idempotency key: the body differs) is safe.
    const legacy = reason.legacyReason.trim().slice(0, PROVIDER_DECLINE_REASON_MAX);
    return postDecline(session, offerId, legacy ? { declineReason: legacy } : {});
  }
}

// Providers no longer mark orders delivered (founder decision 2026-10-27): they
// mark them shipped by recording the shipment, and delivery is the receiver's
// confirmation or automatic. POST /v1/provider/jobs/:jobId/mark-delivered now
// answers 410 provider_mark_delivered_removed, so no client calls it.

export type ProviderJobWithdrawResponse = {
  ok: true;
  command?: {
    accepted?: boolean;
    jobId?: string;
    state?: string | null;
    eventId?: string;
  };
};

/**
 * Self-service "unable to fulfill" withdrawal (2026-08-09): the assigned
 * provider gives an accepted order back. ALWAYS records a fulfillment strike
 * (the UI states this before confirming); the order re-enters matching
 * automatically. Backed by POST /v1/provider/jobs/:jobId/withdraw
 * (withProviderActor, jobs:write). Requires a reason (3..500 chars).
 */
export function withdrawProviderJob(
  session: Session,
  jobId: string,
  reason: string
): Promise<ProviderJobWithdrawResponse> {
  return tinkrApiFetch<ProviderJobWithdrawResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/withdraw`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("provider-withdraw") },
      body: JSON.stringify({ reason: reason.trim() })
    }
  );
}

export type ProviderJobStartPrintingResponse = {
  ok: true;
  command?: {
    accepted?: boolean;
    jobId?: string;
    state?: string | null;
    eventId?: string;
  };
};

/**
 * ADR-M3 §4: the assigned provider marks a quote-locked job as printing. Backed
 * by POST /v1/provider/jobs/:jobId/start-printing (withProviderActor, jobs:write).
 */
export function startProviderJobPrinting(
  session: Session,
  jobId: string,
  reason?: string
): Promise<ProviderJobStartPrintingResponse> {
  const body = reason?.trim()
    ? JSON.stringify({ reason: reason.trim() })
    : JSON.stringify({});
  return tinkrApiFetch<ProviderJobStartPrintingResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/start-printing`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("provider-start-printing") },
      body
    }
  );
}

// ADR-M3 §4: provider print-evidence photos.

export type ProviderEvidenceUploadIntentResponse = {
  ok: true;
  intent: {
    id: string;
    bucket: string;
    path: string;
    artifactType: "image";
    expectedSizeBytes: number;
    maxSizeBytes: number;
    expiresAt: string;
  };
  upload: {
    mode: "supabase_signed_upload_url";
    // A path to the GET signed-upload route. The signed URL itself is NOT in this
    // POST response (it would land in the idempotency cache); fetch it from the
    // GET route right before the PUT.
    signedUploadPath: string;
    method: "PUT";
    contentTypeAccepted: string[];
    cacheControl: string;
  };
  limits: {
    uploadIntentTtlSeconds: number;
    signedUrlTtlSeconds: number;
    maxPerJob: number;
  };
};

export type ProviderEvidenceSignedUploadResponse = {
  ok: true;
  signedUpload: {
    method: "PUT";
    signedUrl: string;
    contentTypeAccepted: string[];
    cacheControl: string;
    expiresInSeconds: number;
  };
};

export type ProviderEvidenceArtifact = {
  id: string;
  byteSize: number | null;
  contentType: string | null;
  verificationStatus: string;
  createdAt: string;
};

export type ProviderEvidenceAttachResponse = {
  ok: true;
  artifact: ProviderEvidenceArtifact;
};

/**
 * A listed photo carries a signed preview URL (15 minutes, minted per read)
 * for browser-renderable verified photos; HEIC and unverified rows get null.
 * The attach response never carries one (it is idempotency-cached).
 */
export type ProviderEvidenceListItem = ProviderEvidenceArtifact & {
  previewUrl: string | null;
};

export type ProviderEvidenceListResponse = {
  ok: true;
  evidence: ProviderEvidenceListItem[];
};

/**
 * Create an evidence upload intent for an assigned job. Returns intent metadata
 * plus a `signedUploadPath` pointer to the GET route that mints the actual signed
 * upload URL — the signed URL is deliberately NOT in this POST response so it
 * never lands in the idempotency cache.
 */
export function createProviderEvidenceUploadIntent(
  session: Session,
  jobId: string,
  input: { expectedSizeBytes: number; originalFilename?: string }
): Promise<ProviderEvidenceUploadIntentResponse> {
  return tinkrApiFetch<ProviderEvidenceUploadIntentResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/evidence/upload-intents`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("provider-evidence-intent") },
      body: JSON.stringify(input)
    }
  );
}

export type ProviderJobShipment = {
  id: string;
  carrier: string;
  trackingNumber: string | null;
  trackingUrl: string | null;
  serviceLevel: "standard" | "tracked" | "express";
  shippedAt: string;
};

export type ProviderJobShipmentResponse = {
  ok: true;
  shipment: ProviderJobShipment | null;
};

export function getProviderJobShipment(
  session: Session,
  jobId: string
): Promise<ProviderJobShipmentResponse> {
  return tinkrApiFetch<ProviderJobShipmentResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/shipment`
  );
}

export function recordProviderJobShipment(
  session: Session,
  jobId: string,
  input: { carrier: string; trackingNumber?: string; trackingUrl?: string }
): Promise<{ ok: true; shipment: ProviderJobShipment }> {
  return tinkrApiFetch(session, `/v1/provider/jobs/${encodeURIComponent(jobId)}/shipment`, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey("provider-record-shipment") },
    body: JSON.stringify(input)
  });
}

export type ProviderJobPrintActuals = {
  id: string;
  tookLongerThanExpected: boolean;
  actualPrintSeconds: number | null;
  actualFilamentG: number | null;
  estimatedPrintSeconds: number | null;
  estimatedFilamentG: number | null;
  reportedAt: string;
};

export type ProviderJobPrintActualsResponse = {
  ok: true;
  printActuals: ProviderJobPrintActuals | null;
};

export function getProviderJobPrintActuals(
  session: Session,
  jobId: string
): Promise<ProviderJobPrintActualsResponse> {
  return tinkrApiFetch<ProviderJobPrintActualsResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/print-actuals`
  );
}

/**
 * Post-print calibration telemetry (founder-authorized 2026-08-27): the
 * assigned provider tells us whether the print exceeded our estimate,
 * optionally with measured print seconds and filament grams. One submission
 * per engagement — the server upserts idempotently and snapshots the estimate
 * it was compared against. Never affects the payout.
 */
export function reportProviderJobPrintActuals(
  session: Session,
  jobId: string,
  input: {
    tookLongerThanExpected: boolean;
    actualPrintSeconds?: number;
    actualFilamentG?: number;
  }
): Promise<{ ok: true; printActuals: ProviderJobPrintActuals }> {
  return tinkrApiFetch(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/print-actuals`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("provider-print-actuals") },
      body: JSON.stringify(input)
    }
  );
}

/**
 * Correct the carrier/tracking of an already-recorded, still-undelivered
 * shipment. Same payload shape as recording; the server freezes the row once
 * delivery is confirmed (rejectionCode shipment_locked).
 */
export function updateProviderJobShipmentTracking(
  session: Session,
  jobId: string,
  input: { carrier: string; trackingNumber?: string; trackingUrl?: string }
): Promise<{ ok: true; shipment: ProviderJobShipment }> {
  return tinkrApiFetch(session, `/v1/provider/jobs/${encodeURIComponent(jobId)}/shipment`, {
    method: "PATCH",
    headers: { "idempotency-key": idempotencyKey("provider-update-shipment") },
    body: JSON.stringify(input)
  });
}

/**
 * Mint the signed upload URL for a provider evidence intent (GET, pure read).
 */
export function fetchProviderEvidenceSignedUpload(
  session: Session,
  jobId: string,
  uploadIntentId: string
): Promise<ProviderEvidenceSignedUploadResponse> {
  return tinkrApiFetch<ProviderEvidenceSignedUploadResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/evidence/upload-intents/${encodeURIComponent(
      uploadIntentId
    )}/signed-upload`
  );
}

/**
 * Attach an uploaded evidence object as a verified image artifact on the job.
 */
export function attachProviderEvidence(
  session: Session,
  jobId: string,
  uploadIntentId: string
): Promise<ProviderEvidenceAttachResponse> {
  return tinkrApiFetch<ProviderEvidenceAttachResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/evidence/attach`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("provider-evidence-attach") },
      body: JSON.stringify({ uploadIntentId })
    }
  );
}

/**
 * List the evidence photos attached to an assigned job, with preview URLs.
 */
export function listProviderEvidence(
  session: Session,
  jobId: string
): Promise<ProviderEvidenceListResponse> {
  return tinkrApiFetch<ProviderEvidenceListResponse>(
    session,
    `/v1/provider/jobs/${encodeURIComponent(jobId)}/evidence`
  );
}

/**
 * Upload an evidence photo end to end: create an intent, fetch a fresh signed
 * upload URL from the GET route, PUT the bytes to it, then attach the object as a
 * verified artifact. The Worker never sees the bytes — they go straight to
 * Supabase Storage. The signed URL is fetched from the GET route (not the POST
 * intent response) so it never enters the idempotency cache.
 */
export async function uploadProviderEvidencePhoto(
  session: Session,
  jobId: string,
  file: File
): Promise<ProviderEvidenceArtifact> {
  const intent = await createProviderEvidenceUploadIntent(session, jobId, {
    expectedSizeBytes: file.size,
    originalFilename: file.name || undefined
  });
  const { signedUpload } = await fetchProviderEvidenceSignedUpload(
    session,
    jobId,
    intent.intent.id
  );
  const put = await fetch(signedUpload.signedUrl, {
    method: "PUT",
    headers: {
      // The API rejects a declared type outside its photo set even when the
      // bytes are fine, so declare the normalized type (an empty MIME type
      // from a file manager falls back to the extension).
      "content-type": evidencePhotoContentType(file) ?? (file.type || "application/octet-stream"),
      "cache-control": signedUpload.cacheControl
    },
    body: file
  });
  if (!put.ok) {
    throw new Error(`evidence upload failed with status ${put.status}`);
  }
  const attached = await attachProviderEvidence(session, jobId, intent.intent.id);
  return attached.artifact;
}

export function listProviderPayables(
  session: Session,
  query: ProviderPayablesQuery = {}
): Promise<ProviderPayablesResponse> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return tinkrApiFetch<ProviderPayablesResponse>(session, `/v1/provider/payables${suffix}`);
}

export function getProviderFulfillmentStanding(
  session: Session
): Promise<ProviderFulfillmentStandingResponse> {
  return tinkrApiFetch<ProviderFulfillmentStandingResponse>(
    session,
    "/v1/provider/fulfillment-standing"
  );
}

export function listProviderQualityIncidents(
  session: Session,
  query: ProviderQualityIncidentsQuery = {},
  signal?: AbortSignal
): Promise<ProviderQualityIncidentsResponse> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.jobId) params.set("jobId", query.jobId);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return tinkrApiFetch<ProviderQualityIncidentsResponse>(
    session,
    `/v1/provider/quality-incidents${suffix}`,
    { signal }
  );
}

export function acknowledgeProviderQualityIncident(
  session: Session,
  incidentId: string,
  reason?: string
): Promise<QualityIncidentCommandResponse> {
  return tinkrApiFetch<QualityIncidentCommandResponse>(
    session,
    `/v1/provider/quality-incidents/${encodeURIComponent(incidentId)}/transition`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-quality-ack")
      },
      body: JSON.stringify({
        command: "acknowledge_incident",
        ...(reason?.trim() ? { reason: reason.trim() } : {})
      })
    }
  );
}

export function respondProviderQualityIncident(
  session: Session,
  incidentId: string,
  providerResponse: string,
  reason?: string
): Promise<QualityIncidentCommandResponse> {
  return tinkrApiFetch<QualityIncidentCommandResponse>(
    session,
    `/v1/provider/quality-incidents/${encodeURIComponent(incidentId)}/transition`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-quality-respond")
      },
      body: JSON.stringify({
        command: "respond_incident",
        providerResponse,
        ...(reason?.trim() ? { reason: reason.trim() } : {})
      })
    }
  );
}

/**
 * The assigned/accepted provider reports a problem on an active job (failed
 * print, unsourceable material, an impossible spec, an unreachable customer).
 * Backed by POST /v1/provider/quality-incidents (withProviderActor, jobs:write,
 * session-actor only). `confirm: true` is the required two-step gate on this
 * dispute-adjacent write. The server validates ownership + actionable state and
 * fixes severity, so the client sends only the job, reason code, and summary.
 */
export function openProviderQualityIncident(
  session: Session,
  input: { jobId: string; reasonCode: QualityIncidentReasonCode; summary: string }
): Promise<QualityIncidentCommandResponse> {
  return tinkrApiFetch<QualityIncidentCommandResponse>(
    session,
    `/v1/provider/quality-incidents`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-quality-open")
      },
      body: JSON.stringify({
        jobId: input.jobId,
        reasonCode: input.reasonCode,
        summary: input.summary,
        confirm: true
      })
    }
  );
}
